import { BlogNewsletterSignupSection } from "@/components/blog/BlogNewsletterSignupSection";
import { FormNotice } from "@/components/forms/FormNotice";
import { RecaptchaField } from "@/components/forms/RecaptchaField";
import { Footer } from "@/components/layout/Footer";
import { GlobalHeroBackground } from "@/components/layout/GlobalHeroBackground";
import { Navbar } from "@/components/layout/Navbar";
import { useSEO } from "@/hooks/useSEO";
import { submitContactForm } from "@/lib/api";
import { recaptchaSiteKey } from "@/lib/recaptcha";
import { cn } from "@/lib/utils";
import { type FormEvent, useState } from "react";


const contactTopics = [
  "Song request",
  "Issue with login",
  "Virtual practice session inquiry",
  "Help with a tutorial",
];

const faqItems = [
  {
    question: "How do the tutorials work?",
    answers: [
      "Once you purchase a tutorial, it will appear on your dashboard when you log in. From there, you'll have full access to everything you need: the complete dance video with music, a step-by-step breakdown tutorial, and a YouTube link to the song.",
      "The tutorial breaks the choreography down into easy-to-follow 8-count sections. It also guides you on when to pause and practice with the music, so you can learn at your own pace — wherever and whenever it works best for you!",
    ],
  },
  {
    question: "Why are the tutorials only available for 4 months?",
    answers: [
      "We've found that four months is the perfect amount of time to learn and master the choreography. It encourages you to dive in and start practicing instead of putting it off.",
      "If you need a little extra time, no problem — you can extend your access by one more week for just $10.",
    ],
  },
  {
    question: "Do you take song requests?",
    answers: [
      "Absolutely! We love hearing what songs you want to see choreographed next. Just fill out the form on our \"Contact Us\" page to send in your request.",
      "While we can't guarantee it'll be ready in time for a specific event, we'll do our best to get to it as soon as we can!",
    ],
  },
  {
    question: "Are the tutorials suitable for beginners?",
    answers: [
      "Yes! Our tutorials are very beginner-friendly. Since they're recordings, you can pause, rewind, and replay the lessons as many times as you need.",
      "Plus, each dance listing includes notes about the experience level, so you can pick the ones that suit you best.",
    ],
  },
  {
    question: "Do you offer in-person teaching?",
    answers: [
      "At the moment, we don't offer in-person lessons. However, we do provide virtual dance practices! Through virtual sessions, we can help you with formations, clean up your choreography, and even coach you on stage presence — all from the comfort of your own space.",
    ],
  },
];

type ContactFormState = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const initialFormState: ContactFormState = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ContactTopicIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 25 24" fill="none" aria-hidden="true">
      <path
        d="M14.526 8.03594L6.12605 16.2859C5.98577 16.4239 5.79688 16.5013 5.60011 16.5013C5.40334 16.5013 5.21445 16.4239 5.07417 16.2859L1.47417 12.7506C1.40394 12.6816 1.34799 12.5993 1.30953 12.5087C1.27107 12.418 1.25085 12.3206 1.25003 12.2221C1.2492 12.1236 1.26778 12.0259 1.30471 11.9346C1.34164 11.8432 1.3962 11.7601 1.46527 11.6898C1.53434 11.6196 1.61656 11.5637 1.70725 11.5252C1.79794 11.4867 1.89531 11.4665 1.99381 11.4657C2.09232 11.4649 2.19002 11.4835 2.28134 11.5204C2.37266 11.5573 2.45581 11.6119 2.52605 11.6809L5.60011 14.6997L13.4751 6.96532C13.6171 6.82583 13.8087 6.74846 14.0077 6.75021C14.1062 6.75109 14.2036 6.77136 14.2943 6.80987C14.3851 6.84839 14.4673 6.9044 14.5364 6.97469C14.6054 7.04499 14.66 7.12821 14.6969 7.21958C14.7338 7.31096 14.7523 7.40871 14.7515 7.50726C14.7506 7.60581 14.7303 7.70322 14.6918 7.79393C14.6533 7.88464 14.5973 7.96688 14.527 8.03594H14.526ZM23.5354 6.97469C23.4664 6.90434 23.3842 6.84829 23.2934 6.80974C23.2027 6.77119 23.1053 6.7509 23.0067 6.75003C22.9082 6.74916 22.8104 6.76772 22.719 6.80467C22.6276 6.84161 22.5444 6.8962 22.4742 6.96532L14.5992 14.6997L12.8339 12.9653C12.6919 12.826 12.5004 12.7487 12.3014 12.7505C12.1025 12.7524 11.9124 12.8332 11.7731 12.9752C11.6337 13.1171 11.5565 13.3087 11.5583 13.5076C11.5602 13.7065 11.641 13.8966 11.7829 14.0359L14.0732 16.2859C14.2135 16.4239 14.4024 16.5013 14.5992 16.5013C14.7959 16.5013 14.9848 16.4239 15.1251 16.2859L23.5251 8.03594C23.5955 7.96697 23.6516 7.88479 23.6903 7.79411C23.7289 7.70343 23.7493 7.60602 23.7502 7.50746C23.7512 7.40889 23.7327 7.31111 23.6959 7.21969C23.659 7.12828 23.6045 7.04502 23.5354 6.97469Z"
        fill="currentColor"
      />
    </svg>
  );
}

function AccordionIcon({ open }: { open: boolean }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3.75 12H20.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {!open && (
        <path
          d="M12 3.75V20.25"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

export function ContactUsPage() {
  useSEO({
    title: "Contact Us",
    description:
      "Get in touch with BollyRhythm for song requests, tutorial help, virtual practice sessions, or any questions about our Bollywood dance tutorials.",
    canonical: "/contact-us",
  });

  const [formState, setFormState] = useState<ContactFormState>(initialFormState);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [openFaqIndices, setOpenFaqIndices] = useState<Set<number>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaInstanceKey, setCaptchaInstanceKey] = useState(0);
  const [errorTarget, setErrorTarget] = useState<"name" | "email" | "message" | "captcha" | "form" | null>(null);
  const captchaConfigured = recaptchaSiteKey.length > 0;

  const handleContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "loading") return;

    const name = formState.name.trim();
    const email = formState.email.trim();
    const message = formState.message.trim();

    if (!name) {
      setStatus("error");
      setErrorTarget("name");
      setErrorMessage("Please add your name so we know who to reply to.");
      return;
    }
    if (!email) {
      setStatus("error");
      setErrorTarget("email");
      setErrorMessage("Please add your email address.");
      return;
    }
    if (!emailPattern.test(email)) {
      setStatus("error");
      setErrorTarget("email");
      setErrorMessage("Use a valid email address so we can get back to you.");
      return;
    }
    if (!message) {
      setStatus("error");
      setErrorTarget("message");
      setErrorMessage("Tell us a little about what you need.");
      return;
    }
    if (!captchaConfigured) {
      setStatus("error");
      setErrorTarget("form");
      setErrorMessage("reCAPTCHA is unavailable right now. Please try again later.");
      return;
    }
    if (!captchaToken) {
      setStatus("error");
      setErrorTarget("captcha");
      setErrorMessage("Check the reCAPTCHA before sending your message.");
      return;
    }

    setStatus("loading");
    setErrorMessage(null);
    setErrorTarget(null);
    const { error } = await submitContactForm({
      name,
      email,
      subject: formState.subject.trim() || undefined,
      message,
    }, captchaToken);
    if (error) {
      setStatus("error");
      setErrorTarget("form");
      setErrorMessage(error.message || "Something went wrong. Please try the reCAPTCHA again in a moment.");
    } else {
      setStatus("success");
      setFormState(initialFormState);
    }

    setCaptchaToken(null);
    setCaptchaInstanceKey((current) => current + 1);
  };

  return (
    <div className="page-wrapper landing-page relative">
      <Navbar />
      <GlobalHeroBackground />

      <main className="landing-page__main main-wrapper">
        <section className="bg-transparent">
          <div className="padding-global">
            <div className="container-large">
              <div className="padding-section-large">
                <div className="text-center mb-24 max-[991px]:mb-[4.5rem] max-[767px]:mb-12">
                  <div className="text-pink-dark tracking-[0.075em] uppercase text-sm font-semibold">contact us</div>
                  <div className="spacer-small" />
                  <h1 className="heading-style-h2 mx-auto text-[#1f0a18]">We'd Love to Hear From You</h1>
                  <div className="spacer-small" />
                  <p>We can help you with:</p>
                  <div className="spacer-medium" />
                  <ul role="list" className="flex flex-wrap justify-center items-start gap-x-8 gap-y-2 m-0 pl-0 list-none max-[767px]:flex-col max-[767px]:items-start max-[767px]:w-fit max-[767px]:mx-auto">
                    {contactTopics.map(topic => (
                      <li key={topic} className="flex items-center justify-start gap-x-2 gap-y-2 text-pink-dark">
                        <div className="w-6 flex flex-none">
                          <ContactTopicIcon />
                        </div>
                        <div>{topic}</div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="max-w-3xl mx-auto">
                  <div id="contact-form" className="border border-yellow-brand bg-white rounded-3xl mb-0 p-12 shadow-[0_0_120px_rgb(242_164_53/10%)] max-[991px]:rounded-[1rem] max-[991px]:p-6 max-[479px]:p-4 w-form">
                    {status === "success" ? (
                      <FormNotice
                        className="text-[#1f0a18]"
                        message="Thank you. Your message is on its way."
                        role="status"
                        tone="success"
                      />
                    ) : (
                    <form
                      id="wf-form-Contact-Form"
                      name="wf-form-Contact-Form"
                      data-name="Contact Form"
                      className="m-0"
                      noValidate
                      onSubmit={handleContactSubmit}
                    >
                      <div className="flex gap-x-3 gap-y-3 max-[767px]:flex-col max-[767px]:gap-x-0 max-[767px]:gap-y-0">
                        <div className="w-full">
                          <label htmlFor="Full-Name" className="block text-neutral-darker text-sm font-medium">
                            Name
                          </label>
                          <input
                            className={`w-full min-h-12 mb-3 border rounded-[0.38rem] bg-transparent text-[#1f0a18] px-4 py-2 font-sans text-base leading-none appearance-none placeholder:text-neutral-light focus:outline-none focus:border-[color-mix(in_srgb,var(--color-pink-dark)_30%,var(--color-neutral-lighter))] ${
                              errorTarget === "name"
                                ? "border-[rgb(118_42_92_/_22%)] bg-[color-mix(in_srgb,#fff8fa_86%,#ffffff)]"
                                : "border-neutral-lighter"
                            }`}
                            maxLength={256}
                            name="Full-Name"
                            data-name="Full Name"
                            placeholder="Full Name"
                            type="text"
                            id="Full-Name"
                            value={formState.name}
                            onChange={event => {
                              setFormState(state => ({ ...state, name: event.target.value }));
                              if (errorTarget === "name") {
                                setErrorTarget(null);
                                setErrorMessage(null);
                                setStatus("idle");
                              }
                            }}
                            aria-invalid={errorTarget === "name"}
                            disabled={status === "loading"}
                          />
                        </div>

                        <div className="w-full">
                          <label htmlFor="Email" className="block text-neutral-darker text-sm font-medium">
                            Email
                          </label>
                          <input
                            className={`w-full min-h-12 mb-3 border rounded-[0.38rem] bg-transparent text-[#1f0a18] px-4 py-2 font-sans text-base leading-none appearance-none placeholder:text-neutral-light focus:outline-none focus:border-[color-mix(in_srgb,var(--color-pink-dark)_30%,var(--color-neutral-lighter))] ${
                              errorTarget === "email"
                                ? "border-[rgb(118_42_92_/_22%)] bg-[color-mix(in_srgb,#fff8fa_86%,#ffffff)]"
                                : "border-neutral-lighter"
                            }`}
                            maxLength={256}
                            name="Email"
                            data-name="Email"
                            placeholder="Email"
                            type="email"
                            id="Email"
                            value={formState.email}
                            onChange={event => {
                              setFormState(state => ({ ...state, email: event.target.value }));
                              if (errorTarget === "email") {
                                setErrorTarget(null);
                                setErrorMessage(null);
                                setStatus("idle");
                              }
                            }}
                            aria-invalid={errorTarget === "email"}
                            disabled={status === "loading"}
                          />
                        </div>
                      </div>

                      <div className="w-full">
                        <label htmlFor="Subject" className="block text-neutral-darker text-sm font-medium">
                          Subject
                        </label>
                        <input
                          className="w-full min-h-12 mb-3 border border-neutral-lighter rounded-[0.38rem] bg-transparent text-[#1f0a18] px-4 py-2 font-sans text-base leading-none appearance-none placeholder:text-neutral-light focus:outline-none focus:border-[color-mix(in_srgb,var(--color-pink-dark)_30%,var(--color-neutral-lighter))]"
                          maxLength={256}
                          name="Subject"
                          data-name="Subject"
                          placeholder="Subject"
                          type="text"
                          id="Subject"
                          value={formState.subject}
                          onChange={event => setFormState(state => ({ ...state, subject: event.target.value }))}
                          disabled={status === "loading"}
                        />
                      </div>

                      <label htmlFor="Message" className="block text-neutral-darker text-sm font-medium">
                        Message
                      </label>
                      <textarea
                        className={`w-full min-h-32 mb-3 border rounded-[0.38rem] bg-transparent text-[#1f0a18] px-4 pt-3 font-sans text-base leading-relaxed resize-y appearance-none placeholder:text-neutral-light focus:outline-none focus:border-[color-mix(in_srgb,var(--color-pink-dark)_30%,var(--color-neutral-lighter))] ${
                          errorTarget === "message"
                            ? "border-[rgb(118_42_92_/_22%)] bg-[color-mix(in_srgb,#fff8fa_86%,#ffffff)]"
                            : "border-neutral-lighter"
                        }`}
                        maxLength={5000}
                        name="Messages"
                        data-name="Messages"
                        placeholder="Tell us about your project..."
                        id="Message"
                        value={formState.message}
                        onChange={event => {
                          setFormState(state => ({ ...state, message: event.target.value }));
                          if (errorTarget === "message") {
                            setErrorTarget(null);
                            setErrorMessage(null);
                            setStatus("idle");
                          }
                        }}
                        disabled={status === "loading"}
                        aria-invalid={errorTarget === "message"}
                      />

                      <RecaptchaField
                        key={captchaInstanceKey}
                        className="mt-6"
                        invalid={errorTarget === "captcha"}
                        onTokenChange={(token) => {
                          setCaptchaToken(token);
                          if (token) {
                            if (errorTarget === "captcha") {
                              setErrorTarget(null);
                              setErrorMessage(null);
                              setStatus("idle");
                            } else if (!errorMessage) {
                              setStatus((current) => current === "error" ? "idle" : current);
                            }
                          }
                        }}
                      />

                      <input type="submit" data-wait="Please wait..." className="w-full mt-3 border-0 rounded-[var(--radius-main)] bg-yellow-brand text-[#1f0a18] py-3 px-6 text-sm font-semibold cursor-pointer transition-colors duration-200 ease-in-out hover:bg-[#e7a53d] disabled:opacity-60 disabled:cursor-not-allowed w-button" value={status === "loading" ? "Please wait..." : "Submit"} disabled={status === "loading" || !captchaConfigured} />
                    </form>
                    )}
                    {status === "error" && (
                      <FormNotice
                        className="mt-4 text-left"
                        message={errorMessage ?? "Oops! Something went wrong while submitting the form."}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-transparent">
          <div className="padding-global">
            <div className="container-large">
              <div className="pt-6 pb-[6.25rem] max-[991px]:pt-4 max-[991px]:pb-24 max-[767px]:pt-2 max-[767px]:pb-16">
                <div className="max-w-[68rem] mx-auto">
                  <h2 className="heading-style-h2 text-color-secondary text-center m-0 mx-auto max-w-[14ch]">Frequently Asked Questions</h2>

                  <div className="flex flex-col gap-[1.38rem] mt-16 max-[767px]:mt-12 max-[767px]:gap-4">
                    {faqItems.map((item, index) => {
                      const isOpen = openFaqIndices.has(index);

                      return (
                        <div
                          key={item.question}
                          className={cn(
                            "rounded-[var(--radius-main)] bg-white px-[4.5rem] pt-6 pb-1 transition-[color,background-color] duration-[0.2s,0.35s] ease-in-out max-[991px]:px-12 max-[767px]:px-6 max-[479px]:p-4 max-[479px]:pb-0",
                            "hover:bg-pink-darker hover:text-white"
                          )}
                        >
                          <button
                            type="button"
                            className="w-full border-0 bg-transparent text-inherit cursor-pointer p-0 text-left font-[inherit]"
                            aria-expanded={isOpen}
                            onClick={() => setOpenFaqIndices(current => {
                              const next = new Set(current);
                              if (next.has(index)) next.delete(index);
                              else next.add(index);
                              return next;
                            })}
                          >
                            <div className="flex items-center justify-between gap-x-4 pb-5 font-display max-[479px]:pb-4">
                              <div className="text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)]">{item.question}</div>
                              <div className="w-6 flex flex-none text-neutral-light group-[.is-open]:text-white/80 [.hover\:bg-pink-darker:hover_&]:text-white/80">
                                <AccordionIcon open={isOpen} />
                              </div>
                            </div>
                          </button>

                          <div className={cn(
                            "grid transition-[grid-template-rows] duration-300 ease-in-out overflow-clip",
                            isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                          )}>
                            <div className="min-h-0">
                              <div className={cn(
                                "shadow-[inset_0_1px_rgb(255_255_255/20%)] text-current opacity-70 pt-5 pb-5 text-base leading-relaxed max-[767px]:text-[0.88rem] max-[479px]:pt-4 max-[479px]:pb-4",
                              )}>
                                {item.answers.map(answer => (
                                  <p key={answer} className="m-0 [&+&]:mt-5">{answer}</p>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <BlogNewsletterSignupSection />
      </main>

      <Footer />
    </div>
  );
}

export default ContactUsPage;
